import React from "react";
import { Shield, Lock } from "lucide-react";
import { BulkCheckbox } from "@/components/bulk/BulkCheckbox";
import { roleLabel } from "./roleLabels";

const STATUS_CHIP = {
    active:    { label: "Active",    cls: "bg-[#E6F1DE] text-[#2E7E22]" },
    suspended: { label: "Suspended", cls: "bg-[#FEF3C7] text-[#B45309]" },
    disabled:  { label: "Disabled",  cls: "bg-[#FEE2E2] text-[#991B1B]" },
    deleted:   { label: "Deleted",   cls: "bg-[#F5F5F7] text-[#6B7280] line-through" },
    invited:   { label: "Invited",   cls: "bg-[#E1E6F0] text-[#131B2F]" },
};

/**
 * A single row in the users master list. Enterprise-console style:
 * name/email primary, role/status chips inline, checkbox on the left
 * for bulk actions.
 */
export default function UserListItem({
    user,
    selected,
    onSelect,
    bulk,
    canBulk,
    disabledReason,
}) {
    const chip = STATUS_CHIP[user.account_status] || STATUS_CHIP.active;
    const lockedNow =
        user.locked_until && new Date(user.locked_until).getTime() > Date.now();

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => onSelect(user.id)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onSelect(user.id)}
            data-testid={`admin-sec-row-${user.id}`}
            className={`group flex items-start gap-3 px-4 py-3 border-b border-[#F0F1F5] cursor-pointer transition-colors ${
                selected
                    ? "bg-[#F0F4FA] border-l-2 border-l-[#131B2F]"
                    : "hover:bg-[#FAFAFC] border-l-2 border-l-transparent"
            }`}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="pt-0.5 shrink-0"
            >
                <BulkCheckbox
                    checked={bulk.has(user.id)}
                    onChange={() => bulk.toggle(user.id)}
                    disabled={!canBulk}
                    ariaLabel={canBulk ? `Select ${user.email}` : disabledReason || "Not selectable"}
                    testId={`bulk-user-checkbox-${user.id}`}
                />
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <div className="text-sm font-medium text-[#131B2F] truncate">
                            <span data-testid={`user-name-${user.id}`}>{user.name || user.email}</span>
                        </div>
                        <div className="text-[11px] text-[#4B5565] truncate">
                            <span data-testid={`user-email-${user.id}`}>{user.email}</span>
                        </div>
                    </div>
                    <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${chip.cls}`}
                    >
                        {chip.label}
                    </span>
                </div>
                <div className="mt-1.5 flex items-center flex-wrap gap-1.5">
                    <span
                        className="inline-flex items-center text-[10px] uppercase tracking-[0.1em] text-[#4B5565] bg-[#F5F5F7] px-1.5 py-0.5 rounded"
                        data-testid={`admin-sec-role-${user.id}`}
                    >
                        {roleLabel(user.role)}
                    </span>
                    {user.is_security_admin && (
                        <span
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#131B2F] bg-[#E1E6F0] px-1.5 py-0.5 rounded"
                            title="Security administrator"
                        >
                            <Shield size={10} /> Sec admin
                        </span>
                    )}
                    {lockedNow && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#B45309] bg-[#FEF3C7] px-1.5 py-0.5 rounded">
                            <Lock size={10} /> Locked
                        </span>
                    )}
                    {user.mfa_enabled && (
                        <span className="inline-flex items-center text-[10px] uppercase tracking-[0.1em] text-[#2E7E22] bg-[#E6F1DE] px-1.5 py-0.5 rounded">
                            MFA
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}
